import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { Decimal, resolveTopupBonuses } from '@mlm/shared';
import type { MyTopup } from '@mlm/shared';
import { apiErrorMessage, fmtPeso, store, topups, wallet } from '../lib/api';
import { qk } from '../lib/queries';
import { instantDay } from '../lib/format';
import {
  Block,
  CoachButton,
  Eyebrow,
  ErrorNote,
  Loading,
  Screen,
  inputClass,
} from '../components/primitives';
import { ShopNav } from '../components/shop-nav';

const STATUS_COPY: Record<MyTopup['status'], string> = {
  pending: 'Waiting for review',
  approved: 'Approved',
  rejected: 'Rejected',
};

/**
 * Putting money in. A top-up is a MANUAL payment: the member pays offline and
 * uploads proof, and an admin approves it. Nothing is credited on this screen.
 *
 * The bonus preview is exactly that. The server re-derives the bonus from the
 * same brackets at approval, so a stale bracket cached on a phone can never
 * become a stale credit.
 */
export function WalletTopupPage() {
  const qc = useQueryClient();
  const [slotId, setSlotId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [methodCode, setMethodCode] = useState('');
  const [reference, setReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState(false);

  const policy = useQuery({
    queryKey: qk.topupPolicy,
    queryFn: topups.policy,
    staleTime: 5 * 60_000,
  });
  const slots = useQuery({ queryKey: qk.mySlots, queryFn: wallet.slots, staleTime: 5 * 60_000 });
  const methods = useQuery({
    queryKey: qk.shopContext,
    queryFn: store.paymentMethods,
    staleTime: 5 * 60_000,
  });
  const mine = useQuery({ queryKey: qk.myTopups, queryFn: topups.mine, staleTime: 30_000 });

  // Top-ups reuse the MANUAL (offline-proof) payment methods. A gateway or
  // wallet method makes no sense here: the whole point is proving an offline
  // payment that has already happened.
  const manual = (methods.data?.methods ?? []).filter((m) => m.settlement === 'manual');
  const active = slotId ?? slots.data?.[0]?.id ?? null;

  // Display only, but computed by the SAME shared function the server credits
  // from, so the preview and the credit can never disagree about a bracket.
  const bonus = useMemo(() => {
    if (!amount || Number(amount) <= 0) return [];
    try {
      return resolveTopupBonuses(policy.data?.brackets ?? [], new Decimal(amount));
    } catch {
      // Half-typed amounts like "12." are not a Decimal yet.
      return [];
    }
  }, [amount, policy.data]);

  const submit = useMutation({
    mutationFn: () =>
      topups.submit({
        slotId: active!,
        method: methodCode,
        amount,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        file: file!,
      }),
    onSuccess: async () => {
      setSent(true);
      setAmount('');
      setReference('');
      setFile(null);
      await qc.invalidateQueries({ queryKey: qk.myTopups });
    },
    onError: setError,
  });

  const noSlot = !slots.isPending && (slots.data?.length ?? 0) === 0;
  const canSubmit =
    Boolean(active) &&
    Boolean(methodCode) &&
    Number(amount) > 0 &&
    file != null &&
    !submit.isPending;

  return (
    <Screen eyebrow="Wallet" title="Top up" wide>
      <ShopNav />

      {policy.isPending || slots.isPending ? (
        <Loading label="Loading" />
      ) : policy.error ? (
        <ErrorNote error={policy.error} retry={() => void policy.refetch()} />
      ) : !policy.data?.enabled ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>Not available</Eyebrow>
          <p className="mt-2 text-[0.93rem] text-ink/70">Top-ups are switched off for this site.</p>
        </div>
      ) : noSlot ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>No wallet yet</Eyebrow>
          <p className="mx-auto mt-2 max-w-sm text-[0.93rem] leading-relaxed text-ink/70">
            A wallet belongs to a membership slot. Activate one first.
          </p>
        </div>
      ) : (
        <>
          {sent && (
            <div className="border-b border-border px-5 py-4">
              <p className="border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] leading-relaxed text-ink/85">
                Sent. Your top-up appears below and is credited once an admin approves it.
              </p>
            </div>
          )}

          <Block eyebrow="To" title="Which slot">
            {(slots.data?.length ?? 0) > 1 ? (
              <select
                aria-label="Slot"
                value={active ?? ''}
                onChange={(e) => setSlotId(e.target.value)}
                className="mt-3 min-h-11 w-full border border-[rgba(16,32,26,0.22)] bg-background px-3 text-[0.93rem] text-ink"
              >
                {slots.data!.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.slotCode}
                  </option>
                ))}
              </select>
            ) : (
              <p className="mt-1">{slots.data?.[0]?.slotCode}</p>
            )}
            {policy.data.settlesCdDebt && (
              <p className="mt-3 text-[0.85rem] leading-relaxed text-sage-deep">
                If this slot still has an entry debt, the top-up pays that down first.
              </p>
            )}
          </Block>

          <Block eyebrow="Amount" title="How much did you pay">
            <input
              aria-label="Amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="0.00"
              className={`${inputClass} mt-3`}
            />
            {bonus.length > 0 && (
              <div className="mt-3 border border-border px-4 py-3">
                <Eyebrow>Bonus at this amount</Eyebrow>
                <ul className="mt-2 space-y-1 text-[0.9rem] text-ink/80">
                  {bonus.map((b) => (
                    <li key={b.bracketId}>
                      {b.bonusCurrency === 'gc' ? 'Gift credits' : 'Cash'}:{' '}
                      {fmtPeso(b.bonus.toString())}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[0.8rem] text-sage-deep">
                  Confirmed when an admin approves the payment.
                </p>
              </div>
            )}
          </Block>

          <Block eyebrow="How" title="Where you paid">
            {methods.isPending ? (
              <Loading label="Loading methods" />
            ) : manual.length === 0 ? (
              <p>No payment method accepts a top-up right now.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {manual.map((m) => (
                  <li key={m.code}>
                    <button
                      type="button"
                      onClick={() => setMethodCode(m.code)}
                      className={[
                        'min-h-11 w-full border px-4 py-3 text-left text-[0.93rem] text-ink transition-colors',
                        m.code === methodCode
                          ? 'border-verde bg-surface'
                          : 'border-[rgba(16,32,26,0.22)] hover:border-[rgba(16,32,26,0.5)]',
                      ].join(' ')}
                    >
                      {m.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {manual.find((m) => m.code === methodCode)?.instructions && (
              <p className="mt-4 border border-border px-4 py-3 text-[0.9rem] leading-relaxed text-ink/80">
                {manual.find((m) => m.code === methodCode)!.instructions}
              </p>
            )}
          </Block>

          <Block eyebrow="Proof" title="Upload your receipt">
            <label className="eyebrow mt-3 block" htmlFor="tu-ref">
              Reference number (optional)
            </label>
            <input
              id="tu-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className={`${inputClass} mt-1`}
            />
            <label className="eyebrow mt-4 block" htmlFor="tu-file">
              Screenshot or photo
            </label>
            <input
              id="tu-file"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-[0.9rem] text-ink file:mr-3 file:border file:border-[rgba(16,32,26,0.22)] file:bg-background file:px-4 file:py-2 file:font-mono file:text-[0.68rem] file:uppercase file:tracking-[0.14em] file:text-ink"
            />
            {file && <p className="mt-2 text-[0.85rem] text-sage-deep">{file.name}</p>}
          </Block>

          <div className="px-5 py-6">
            {error != null && (
              <p className="mb-4 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] text-ink/85">
                {apiErrorMessage(error)}
              </p>
            )}
            <CoachButton
              kind="gold"
              block
              disabled={!canSubmit}
              onClick={() => {
                setError(null);
                setSent(false);
                submit.mutate();
              }}
            >
              <Upload className="h-4 w-4" aria-hidden />
              {submit.isPending ? 'Sending' : 'Submit for review'}
            </CoachButton>
          </div>
        </>
      )}

      <p className="border-b border-t border-border px-5 py-3 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-sage-deep">
        Your top-ups
      </p>
      {mine.isPending ? (
        <Loading label="Loading" />
      ) : (mine.data?.length ?? 0) === 0 ? (
        <p className="px-5 py-10 text-center text-[0.93rem] text-ink/70">Nothing yet.</p>
      ) : (
        <ul>
          {mine.data!.map((t) => (
            <li
              key={t.id}
              className="flex items-start justify-between gap-4 border-b border-border px-5 py-4"
            >
              <div className="min-w-0">
                <p className="text-[0.93rem] text-ink">{fmtPeso(t.amount)}</p>
                <p className="mt-1 text-[0.85rem] text-sage-deep">
                  {STATUS_COPY[t.status]} · {instantDay(t.createdAt)}
                </p>
                {t.status === 'rejected' && t.rejectionReason && (
                  <p className="mt-1 text-[0.85rem] text-ink/70">{t.rejectionReason}</p>
                )}
                {t.status === 'approved' && (t.bonusBreakdown?.length ?? 0) > 0 && (
                  <p className="mt-1 text-[0.85rem] text-verde">
                    Bonus{' '}
                    {t
                      .bonusBreakdown!.map(
                        (b) => `${fmtPeso(b.amount)}${b.currency === 'gc' ? ' GC' : ''}`,
                      )
                      .join(' + ')}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Screen>
  );
}
